import React, { useState, useEffect } from 'react';
import { listingTableSx } from '../components/listingTableSx';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  LinearProgress,
  Avatar,
  Badge,
  Stack,
  Divider
} from '@mui/material';
import {
  Security as SecurityIcon,
  AccountTree as AccountTreeIcon,
  Timeline as TimelineIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Tag as TagIcon,
  Analytics as AnalyticsIcon,
  Shield as ShieldIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Computer as ComputerIcon,
  Storage as StorageIcon,
  Cloud as CloudIcon,
  VpnKey as VpnKeyIcon
} from '@mui/icons-material';
import { pamAPI } from '../services/api';
import { PAMAccount, PAMUsageLog, PAMDependency, PAMComplianceReport, PAMUsageAnalytics } from '../types';
import { useNotification } from '../contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';
import { isMercuryTenant } from '../utils/tenantBranding';
import { ListingSortSelect } from '../components/ListingPagination';
import { DEFAULT_LISTING_SORT, ListingSortValue, sortListing } from '../utils/listingSort';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`pam-tabpanel-${index}`}
      aria-labelledby={`pam-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const PAMDashboard: React.FC = () => {
  const { showError, showSuccess } = useNotification();
  const navigate = useNavigate();
  const hideDependencyGraph = isMercuryTenant();
  const pamTabUiIndex = (panelIndex: number) =>
    hideDependencyGraph && panelIndex > 2 ? panelIndex - 1 : panelIndex;
  const pamTabPanelIndex = (uiIndex: number) =>
    hideDependencyGraph && uiIndex >= 2 ? uiIndex + 1 : uiIndex;

  // State management
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [accounts, setAccounts] = useState<PAMAccount[]>([]);
  const [usageLogs, setUsageLogs] = useState<PAMUsageLog[]>([]);
  const [dependencies, setDependencies] = useState<PAMDependency[]>([]);
  const [complianceReport, setComplianceReport] = useState<PAMComplianceReport | null>(null);
  const [usageAnalytics, setUsageAnalytics] = useState<PAMUsageAnalytics | null>(null);
  
  // Filter states
  const [classificationFilter, setClassificationFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [riskFilter, setRiskFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [listingSort, setListingSort] = useState<ListingSortValue>(DEFAULT_LISTING_SORT);
  
  // Dialog states
  const [selectedAccount, setSelectedAccount] = useState<PAMAccount | null>(null);
  const [checkoutReason, setCheckoutReason] = useState('');
  const [checkoutStart, setCheckoutStart] = useState('');
  const [checkoutEnd, setCheckoutEnd] = useState('');
  const [checkoutMessage, setCheckoutMessage] = useState('');
  const [accountDetailsOpen, setAccountDetailsOpen] = useState(false);
  const [metadataDialogOpen, setMetadataDialogOpen] = useState(false);
  const [newTags, setNewTags] = useState<string[]>([]);
  const [newMetadata, setNewMetadata] = useState<Record<string, any>>({});

  // Load initial data
  useEffect(() => {
    loadPAMData();
  }, []);

  useEffect(() => {
    if (hideDependencyGraph && activeTab === 2) {
      setActiveTab(0);
    }
  }, [hideDependencyGraph, activeTab]);

  const loadPAMData = async () => {
    try {
      setLoading(true);
      
      const [
        accountsResponse,
        usageLogsResponse,
        dependenciesResponse,
        complianceResponse,
        analyticsResponse
      ] = await Promise.all([
        pamAPI.getAccounts(),
        pamAPI.getUsageLogs(),
        pamAPI.getDependencies(),
        pamAPI.getComplianceReport(),
        pamAPI.getUsageAnalytics()
      ]);

      if (accountsResponse.data.success) {
        setAccounts(accountsResponse.data.data);
      }
      
      if (usageLogsResponse.data.success) {
        setUsageLogs(usageLogsResponse.data.data);
      }
      
      if (dependenciesResponse.data.success) {
        setDependencies(dependenciesResponse.data.data);
      }
      
      if (complianceResponse.data.success) {
        setComplianceReport(complianceResponse.data.data);
      }
      
      if (analyticsResponse.data.success) {
        setUsageAnalytics(analyticsResponse.data.data);
      }
      
    } catch (error) {
      console.error('Error loading PAM data:', error);
      showError('Failed to load PAM data');
    } finally {
      setLoading(false);
    }
  };

  // Filter accounts based on current filters
  const filteredAccounts = accounts.filter(account => {
    const matchesClassification = classificationFilter === 'all' || account.classification === classificationFilter;
    const matchesStatus = statusFilter === 'all' || account.status === statusFilter;
    const matchesRisk = riskFilter === 'all' || 
      (riskFilter === 'low' && account.risk_score < 4) ||
      (riskFilter === 'medium' && account.risk_score >= 4 && account.risk_score < 7) ||
      (riskFilter === 'high' && account.risk_score >= 7);
    const matchesSearch = searchTerm === '' || 
      account.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.application_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.owner_name.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesClassification && matchesStatus && matchesRisk && matchesSearch;
  });
  const sortedAccounts = sortListing(filteredAccounts, listingSort);

  // Get account type icon
  const getAccountTypeIcon = (accountType: string) => {
    switch (accountType) {
      case 'service': return <PersonIcon />;
      case 'database': return <StorageIcon />;
      case 'os': return <ComputerIcon />;
      case 'api': return <VpnKeyIcon />;
      case 'cloud_iam': return <CloudIcon />;
      default: return <SecurityIcon />;
    }
  };

  // Get risk score color
  const getRiskScoreColor = (riskScore: number) => {
    if (riskScore < 4) return 'success';
    if (riskScore < 7) return 'warning';
    return 'error';
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      case 'expired': return 'error';
      case 'rotated': return 'info';
      default: return 'default';
    }
  };

  // Handle account details view
  const handleViewAccountDetails = (account: PAMAccount) => {
    setSelectedAccount(account);
    setAccountDetailsOpen(true);
  };

  // Handle metadata edit
  const handleEditMetadata = (account: PAMAccount) => {
    setSelectedAccount(account);
    setNewTags([...account.tags]);
    setNewMetadata({ ...account.metadata });
    setMetadataDialogOpen(true);
  };

  // Save metadata changes
  const handleSaveMetadata = async () => {
    if (!selectedAccount) return;

    try {
      await pamAPI.updateAccountMetadata(selectedAccount.id, newMetadata, newTags);
      showSuccess('Account metadata updated successfully');
      setMetadataDialogOpen(false);
      loadPAMData(); // Refresh data
    } catch (error) {
      console.error('Error updating metadata:', error);
      showError('Failed to update account metadata');
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const rotationPhrase = (days: number) => {
    if (days < 0) return `${Math.abs(days)} days overdue`;
    if (days === 0) return 'Due today';
    return `${days} days`;
  };

  const getDaysUntilExpiry = (nextRotation: string) => {
    const now = new Date();
    const expiry = new Date(nextRotation);
    const diffTime = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading PAM Dashboard...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Privileged Access Management
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Unified inventory and management of privileged accounts
          </Typography>
        </Box>
        {!hideDependencyGraph && (
          <Button
            variant="outlined"
            startIcon={<AccountTreeIcon />}
            onClick={() => navigate('/dependency-graph')}
            sx={{ mr: 2 }}
          >
            View Graph
          </Button>
        )}
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadPAMData}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Compliance Overview Cards */}
      {complianceReport && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <SecurityIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Total Accounts</Typography>
                </Box>
                <Typography variant="h4" color="primary">
                  {complianceReport.total_accounts}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  PAM-managed accounts
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                  <Typography variant="h6">Compliance</Typography>
                </Box>
                <Typography variant="h4" color={complianceReport.compliance_percentage >= 100 ? 'success.main' : 'warning.main'}>
                  {complianceReport.compliance_percentage}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Inside rotation policy
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <WarningIcon color="warning" sx={{ mr: 1 }} />
                  <Typography variant="h6">High Risk</Typography>
                </Box>
                <Typography variant="h4" color="warning.main">
                  {complianceReport.high_risk_accounts}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Risk score ≥ 7
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ErrorIcon color="error" sx={{ mr: 1 }} />
                  <Typography variant="h6">Stale Accounts</Typography>
                </Box>
                <Typography variant="h4" color="error.main">
                  {complianceReport.stale_accounts}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Unused &gt; 30 days
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Main Content Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={pamTabUiIndex(activeTab)}
          onChange={(_, uiIndex) => setActiveTab(pamTabPanelIndex(uiIndex))}
        >
          <Tab label="Unified Inventory" icon={<SecurityIcon />} />
          <Tab label="Ownership Mapping" icon={<PersonIcon />} />
          {!hideDependencyGraph && <Tab label="Dependency Graph" icon={<AccountTreeIcon />} />}
          <Tab label="Access Insights" icon={<TimelineIcon />} />
          <Tab label="Analytics & Reports" icon={<AnalyticsIcon />} />
          <Tab label="Risk & Security" icon={<ShieldIcon />} />
        </Tabs>
      </Box>

      {/* Tab 1: Unified Inventory */}
      <TabPanel value={activeTab} index={0}>
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                placeholder="Search accounts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Classification</InputLabel>
                <Select
                  value={classificationFilter}
                  onChange={(e) => setClassificationFilter(e.target.value)}
                  label="Classification"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="App">App</MenuItem>
                  <MenuItem value="DB">DB</MenuItem>
                  <MenuItem value="OS/Infra">OS/Infra</MenuItem>
                  <MenuItem value="Service/API">Service/API</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="expired">Expired</MenuItem>
                  <MenuItem value="rotated">Rotated</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Risk Level</InputLabel>
                <Select
                  value={riskFilter}
                  onChange={(e) => setRiskFilter(e.target.value)}
                  label="Risk Level"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="low">Low (0-3)</MenuItem>
                  <MenuItem value="medium">Medium (4-6)</MenuItem>
                  <MenuItem value="high">High (7-10)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <ListingSortSelect value={listingSort} onChange={setListingSort} />
            </Grid>
          </Grid>
        </Box>

        {sortedAccounts.some((account) => account.status === 'expired' || getDaysUntilExpiry(account.password_next_rotation) < 0) && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Overdue accounts stay overdue until Rotate or Disable is confirmed in account details.
            {sortedAccounts
              .filter((account) => account.status === 'expired' || getDaysUntilExpiry(account.password_next_rotation) < 0)
              .map((account) => (
                <Box key={account.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mt: 1 }}>
                  <Typography variant="body2">
                    {account.name} · due {formatDate(account.password_next_rotation)} · {account.owner_name} · {account.rotation_policy}
                  </Typography>
                  <Button size="small" onClick={() => handleViewAccountDetails(account)}>Open</Button>
                </Box>
              ))}
          </Alert>
        )}
        <TableContainer component={Paper}>
          <Table sx={listingTableSx}>
            <TableHead>
              <TableRow>
                <TableCell>Account</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Classification</TableCell>
                <TableCell>Application</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Risk Score</TableCell>
                <TableCell>Last Used</TableCell>
                <TableCell>Next Rotation</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedAccounts.map((account) => {
                const daysUntilExpiry = getDaysUntilExpiry(account.password_next_rotation);
                return (
                  <TableRow key={account.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                          {getAccountTypeIcon(account.account_type)}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {account.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {account.id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={account.account_type} 
                        size="small" 
                        variant="outlined"
                        icon={getAccountTypeIcon(account.account_type)}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={account.classification} 
                        size="small" 
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{account.application_name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {account.owner_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {account.owner_email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={account.status} 
                        size="small" 
                        color={getStatusColor(account.status) as any}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={account.risk_score} 
                        size="small" 
                        color={getRiskScoreColor(account.risk_score) as any}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(account.last_used)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        by {account.last_used_by}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {formatDate(account.password_next_rotation)}
                        </Typography>
                        {daysUntilExpiry <= 7 && (
                          <Chip 
                            label={rotationPhrase(daysUntilExpiry)} 
                            size="small" 
                            color={daysUntilExpiry < 0 ? 'error' : 'warning'}
                            icon={<WarningIcon />}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton 
                          size="small" 
                          onClick={() => handleViewAccountDetails(account)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Metadata">
                        <IconButton 
                          size="small" 
                          onClick={() => handleEditMetadata(account)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Tab 2: Ownership Mapping */}
      <TabPanel value={activeTab} index={1}>
        <Typography variant="h6" gutterBottom>
          Account Ownership Mapping
        </Typography>
        <TableContainer component={Paper}>
          <Table sx={listingTableSx}>
            <TableHead>
              <TableRow>
                <TableCell>Account</TableCell>
                <TableCell>Application</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Server/Infrastructure</TableCell>
                <TableCell>Database</TableCell>
                <TableCell>Usage Context</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedAccounts.map((account) => (
                <TableRow key={account.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                        {getAccountTypeIcon(account.account_type)}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {account.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {account.classification}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{account.application_name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {account.owner_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {account.owner_email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {account.server_name || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {account.database_name || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {account.metadata.business_context || 'Not specified'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Tab 3: Dependency Graph */}
      {!hideDependencyGraph && (
      <TabPanel value={activeTab} index={2}>
        <Typography variant="h6" gutterBottom>
          Application → Server → Database → Account Dependencies
        </Typography>
        <TableContainer component={Paper}>
          <Table sx={listingTableSx}>
            <TableHead>
              <TableRow>
                <TableCell>Application</TableCell>
                <TableCell>Server</TableCell>
                <TableCell>Database</TableCell>
                <TableCell>Account</TableCell>
                <TableCell>Account Type</TableCell>
                <TableCell>Dependency Type</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dependencies.map((dependency) => (
                <TableRow key={dependency.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {dependency.application_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{dependency.server_name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {dependency.database_name || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                        {getAccountTypeIcon(dependency.account_type)}
                      </Avatar>
                      <Typography variant="body2" fontWeight="medium">
                        {dependency.account_name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={dependency.account_type} 
                      size="small" 
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={dependency.dependency_type} 
                      size="small" 
                      color={dependency.dependency_type === 'direct' ? 'primary' : 'secondary'}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>
      )}

      {/* Tab 4: Access Insights */}
      <TabPanel value={activeTab} index={3}>
        <Typography variant="h6" gutterBottom>
          Recent Access Request Insights
        </Typography>
        <TableContainer component={Paper}>
          <Table sx={listingTableSx}>
            <TableHead>
              <TableRow>
                <TableCell>Account</TableCell>
                <TableCell>Used By</TableCell>
                <TableCell>Usage Type</TableCell>
                <TableCell>Timestamp</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Success</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {usageLogs.slice(0, 20).map((log) => (
                <TableRow key={log.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {log.account_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {log.used_by_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {log.used_by}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={log.usage_type} 
                      size="small" 
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(log.timestamp)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {log.duration_minutes ? `${log.duration_minutes} min` : 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={log.success ? 'Success' : 'Failed'} 
                      size="small" 
                      color={log.success ? 'success' : 'error'}
                      icon={log.success ? <CheckCircleIcon /> : <ErrorIcon />}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {log.details}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Tab 5: Analytics & Reports */}
      <TabPanel value={activeTab} index={4}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Usage Analytics (Last 30 Days)
                </Typography>
                {usageAnalytics && (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2">Total Usage</Typography>
                      <Typography variant="h6">{usageAnalytics.total_usage}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2">Successful</Typography>
                      <Typography variant="h6" color="success.main">
                        {usageAnalytics.successful_usage}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2">Failed</Typography>
                      <Typography variant="h6" color="error.main">
                        {usageAnalytics.failed_usage}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Avg Duration</Typography>
                      <Typography variant="h6">
                        {Math.round(usageAnalytics.average_duration_minutes)} min
                      </Typography>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Password Rotation Status
                </Typography>
                {complianceReport && (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2">Recently Rotated</Typography>
                      <Typography variant="h6" color="success.main">
                        {complianceReport.rotated_accounts}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2">Expired</Typography>
                      <Typography variant="h6" color="error.main">
                        {complianceReport.expired_accounts}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Compliance Rate</Typography>
                      <Typography variant="h6" color="primary.main">
                        {complianceReport.compliance_percentage}%
                      </Typography>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Tab 6: Risk & Security */}
      <TabPanel value={activeTab} index={5}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="error">
                  High-Risk Accounts
                </Typography>
                <TableContainer>
                  <Table size="small" sx={listingTableSx}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Account</TableCell>
                        <TableCell>Risk Score</TableCell>
                        <TableCell>Last Used</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {accounts.filter(acc => acc.risk_score >= 7).map((account) => (
                        <TableRow key={account.id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {account.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={account.risk_score} 
                              size="small" 
                              color="error"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {formatDate(account.last_used)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="warning">
                  Rotation due or overdue
                </Typography>
                <TableContainer>
                  <Table size="small" sx={listingTableSx}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Account</TableCell>
                        <TableCell>Rotation</TableCell>
                        <TableCell>Owner</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {accounts.filter(acc => getDaysUntilExpiry(acc.password_next_rotation) <= 7).map((account) => (
                        <TableRow key={account.id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {account.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={rotationPhrase(getDaysUntilExpiry(account.password_next_rotation))} 
                              size="small" 
                              color={getDaysUntilExpiry(account.password_next_rotation) < 0 ? 'error' : 'warning'}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {account.owner_name}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Account Details Dialog */}
      <Dialog 
        open={accountDetailsOpen} 
        onClose={() => setAccountDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Account Details - {selectedAccount?.name}
        </DialogTitle>
        <DialogContent>
          {selectedAccount && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Account ID</Typography>
                  <Typography variant="body2">{selectedAccount.id}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Type</Typography>
                  <Typography variant="body2">{selectedAccount.account_type}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Classification</Typography>
                  <Typography variant="body2">{selectedAccount.classification}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Chip 
                    label={selectedAccount.status} 
                    size="small" 
                    color={getStatusColor(selectedAccount.status) as any}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Risk Score</Typography>
                  <Chip 
                    label={selectedAccount.risk_score} 
                    size="small" 
                    color={getRiskScoreColor(selectedAccount.risk_score) as any}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Owner</Typography>
                  <Typography variant="body2">{selectedAccount.owner_name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Due</Typography>
                  <Typography variant="body2">{formatDate(selectedAccount.password_next_rotation)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Rotation Policy</Typography>
                  <Typography variant="body2">{selectedAccount.rotation_policy}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Tags</Typography>
                  <Box sx={{ mt: 1 }}>
                    {selectedAccount.tags.map((tag, index) => (
                      <Chip key={index} label={tag} size="small" sx={{ mr: 1, mb: 1 }} />
                    ))}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Business Context</Typography>
                  <Typography variant="body2">
                    {selectedAccount.metadata.business_context || 'Not specified'}
                  </Typography>
                </Grid>
              </Grid>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Checkout</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Opening this account does not check it out. A reason and a window are required.
                  {selectedAccount.risk_score >= 8 ? ' This account needs a second approver.' : ''}
                </Typography>
                <TextField size="small" fullWidth label="Reason" value={checkoutReason} onChange={(e) => setCheckoutReason(e.target.value)} sx={{ mb: 1 }} />
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField size="small" type="date" label="Starts" InputLabelProps={{ shrink: true }} value={checkoutStart} onChange={(e) => setCheckoutStart(e.target.value)} />
                  <TextField size="small" type="date" label="Ends" InputLabelProps={{ shrink: true }} value={checkoutEnd} onChange={(e) => setCheckoutEnd(e.target.value)} />
                </Box>
                {checkoutMessage && <Typography variant="body2" sx={{ mb: 1 }}>{checkoutMessage}</Typography>}
                <Button
                  size="small"
                  variant="outlined"
                  onClick={async () => {
                    const result = await pamAPI.checkoutAccount(selectedAccount.id, {
                      reason: checkoutReason,
                      starts_at: checkoutStart,
                      ends_at: checkoutEnd,
                    });
                    setCheckoutMessage(result.data?.message || 'Checkout recorded.');
                  }}
                >
                  Request checkout
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            color="warning"
            disabled={!selectedAccount || selectedAccount.status === 'inactive'}
            onClick={async () => {
              if (!selectedAccount) return;
              const confirmed = window.confirm(`Disable ${selectedAccount.name}? Opening this dialog does not change the account.`);
              if (!confirmed) return;
              await pamAPI.applyAccountAction(selectedAccount.id, 'disable');
              setAccountDetailsOpen(false);
              await loadPAMData();
            }}
          >
            Disable
          </Button>
          <Button
            variant="contained"
            disabled={!selectedAccount}
            onClick={async () => {
              if (!selectedAccount) return;
              const confirmed = window.confirm(`Record a password rotation for ${selectedAccount.name}? The next rotation moves 90 days ahead.`);
              if (!confirmed) return;
              await pamAPI.applyAccountAction(selectedAccount.id, 'rotate');
              setAccountDetailsOpen(false);
              await loadPAMData();
            }}
          >
            Rotate
          </Button>
          <Button onClick={() => setAccountDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Metadata Edit Dialog */}
      <Dialog 
        open={metadataDialogOpen} 
        onClose={() => setMetadataDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit Metadata - {selectedAccount?.name}
        </DialogTitle>
        <DialogContent>
          {selectedAccount && (
            <Box sx={{ pt: 1 }}>
              <TextField
                fullWidth
                label="Business Context"
                multiline
                rows={3}
                value={newMetadata.business_context || ''}
                onChange={(e) => setNewMetadata({ ...newMetadata, business_context: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={newMetadata.description || ''}
                onChange={(e) => setNewMetadata({ ...newMetadata, description: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Environment"
                value={newMetadata.environment || ''}
                onChange={(e) => setNewMetadata({ ...newMetadata, environment: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Tags (comma-separated)"
                value={newTags.join(', ')}
                onChange={(e) => setNewTags(e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag))}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMetadataDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveMetadata} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PAMDashboard;
